import { CopyAllOutlined, OpenInNewOutlined } from '@mui/icons-material'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DialogCloseSaveButtons } from 'components/Common/DialogActionButtons/DialogCloseSaveButtons'
import { ProjectsController } from 'controllers/Projects/ProjectsController'
import { useAppController } from 'customHooks/useAppController'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useAppSelector } from 'store/configureStore'
import { BomItemMenuItem } from './BomItemMenuItem'

export const ReCreateProjectConfirmationDialog = (props: {
  open: boolean
  onClose: () => void
}) => {
  const { t } = useTranslation('project')

  const [stage, setStage] = useState<
    'confirmation' | 'recreating' | 'success' | 'error' | 'end'
  >('confirmation')

  const [reCreateProjectId, setReCreateProjectId] = useState<string | null>(
    null,
  )

  const projectReference = useAppSelector(
    projectSelectors.projectReferenceSelector,
  )
  const orderNumber = useAppSelector(
    projectSelectors.projectOrderNumberSelector,
  )

  const [newProjectReference, setNewProjectReference] =
    useState<string>(projectReference)
  const [newOrderNumber, setNewOrderNumber] = useState<string>(orderNumber)

  const { controller, loading, error } = useAppController(
    () => new ProjectsController(),
  )

  const navigate = useNavigate()

  const projectId = useAppSelector(projectSelectors.projectIdSelector)

  const { onClose } = props

  const message = useMemo(() => {
    let message: ReactNode = ''
    switch (stage) {
      case 'confirmation':
        message = (
          <>
            <Typography variant="body2">
              {t(
                'project:re-create-project-message-2',
                'This action will re create the current project. You can change the project reference and order number of the new project before proceeding.',
              )}
            </Typography>

            <div>
              <TextField
                label={t('project:project-reference', 'Project Reference')}
                fullWidth
                placeholder={projectReference}
                margin="normal"
                value={newProjectReference}
                onChange={(e) => setNewProjectReference(e.target.value)}
                onFocus={(e) => e.target.select()}
                required
              />
              <TextField
                label={t('project:order-number', 'Order Number')}
                fullWidth
                margin="normal"
                placeholder={orderNumber}
                value={newOrderNumber}
                onChange={(e) => setNewOrderNumber(e.target.value)}
                onFocus={(e) => e.target.select()}
                required
              />
            </div>
          </>
        )
        break
      case 'recreating':
        message = (
          <>
            <Typography variant="body1">
              {t(
                'project:re-create-project-in-progress',
                'The project is being recreated. This may take a few moments.',
              )}
            </Typography>
          </>
        )
        break
      case 'success':
        message = (
          <>
            {t(
              'project:re-create-project-success-message',
              'A new project was created successfully.',
            )}
            <Typography
              sx={{ textAlign: 'center' }}
              style={{ marginTop: '2rem' }}
            >
              <a
                href={`${reCreateProjectId}`}
                onClick={(e) => {
                  e.preventDefault()
                  navigate(`${reCreateProjectId}`)
                  onClose()
                }}
              >
                {t(
                  'project:re-create-project-success-link',
                  'click here to open the new project.',
                )}
              </a>
              <Tooltip title={t('common:open-in-new-tab', 'open in new tab')}>
                <IconButton
                  onClick={() => {
                    window.open(`${reCreateProjectId}`, '_blank')
                  }}
                  size="small"
                >
                  <OpenInNewOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
          </>
        )
        break
      case 'error':
        message = (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              height: '100%',
            }}
          >
            <Typography variant="caption">
              {t(
                'project:re-create-project-error-message',
                'An error occurred while recreating the project.',
              )}
            </Typography>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body1" color="error">
                {error['re-create-project']?.message.length > 200
                  ? t(
                      'errors:internal-error-try-later',
                      'internal error, please retry in a few moments',
                    )
                  : error['re-create-project'].message}
              </Typography>
            </div>
          </div>
        )
        break
    }

    return message
  }, [
    error,
    navigate,
    newOrderNumber,
    newProjectReference,
    onClose,
    orderNumber,
    projectReference,
    reCreateProjectId,
    stage,
    t,
  ])

  useEffect(() => {
    switch (stage) {
      case 'recreating': {
        controller
          .ReCreateProject(projectId, {
            newOrderNumber: newOrderNumber ? newOrderNumber : orderNumber,
            newProjectReference: newProjectReference
              ? newProjectReference
              : projectReference,
          })
          .then((redirectionLink) => {
            setReCreateProjectId(redirectionLink)
            setStage('success')
          })
          .catch((err) => {
            console.error(err)
            setStage('error')
          })
        break
      }
    }
  }, [
    controller,
    reCreateProjectId,
    navigate,
    projectId,
    stage,
    newOrderNumber,
    newProjectReference,
    orderNumber,
    projectReference,
  ])

  const handleContinue = useCallback(async () => {
    switch (stage) {
      case 'confirmation': {
        setStage('recreating')
        break
      }
      case 'success': {
        if (!reCreateProjectId) {
          console.warn('No copied project ID available.')
          setStage('end')
          return
        }

        props.onClose()
        break
      }
      case 'error': {
        props.onClose()
      }
    }
  }, [reCreateProjectId, props, stage])

  const buttonTranslationKeys = useMemo(() => {
    const texts = {
      ok: 'common:ok',
      cancel: 'common:cancel',
    }

    switch (stage) {
      case 'success': {
        texts.cancel = 'common:no'
        break
      }
      case 'error': {
        texts.ok = 'common:close'
        break
      }
    }

    return texts
  }, [stage])

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        console.info('Dialog closed')
        props.onClose()
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {t('project:re-create-project', 'recreate project')}
      </DialogTitle>
      <DialogContent
        style={{
          minHeight: '100px',
        }}
      >
        {message}
      </DialogContent>
      <DialogActions>
        <DialogCloseSaveButtons
          onCloseButtonClicked={props.onClose}
          onSaveButtonClicked={handleContinue}
          saveButtonTranslationKey={buttonTranslationKeys.ok}
          cancelButtonTranslationKey={buttonTranslationKeys.cancel}
          saveButtonDisabled={loading['re-create-project']}
          cancelButtonDisabled={loading['re-create-project']}
          saving={loading['re-create-project']}
          cancelButtonInvisible={stage === 'error' || stage === 'success'}
        />
      </DialogActions>
    </Dialog>
  )
}

export const ReCreateProjectMenuItem = ({
  onClose,
}: {
  onClose: () => void
}) => {
  const { openDialog } = useDialogManager()

  return (
    <>
      <span>
        <BomItemMenuItem
          dataTestId="project-menu-recreate-project"
          onClick={() => openDialog('ReCreateProjectConfirmationDialog')}
          primaryTextTranslationKey="project:re-create-project"
          secondaryTextTranslationKey="project:re-create-project-secondary"
          icon={<CopyAllOutlined />}
          closeOnClick
          // loading={loading['re-create-project']}
          onClose={onClose}
        />
      </span>
    </>
  )
}
